import React, { useEffect, useState, useMemo, useRef } from "react";
import { confirm } from "../utils/confirm";
import { collection, query, where, onSnapshot, doc, addDoc, updateDoc, deleteDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { Plus, X, FileText, ChevronDown, ChevronRight, Eye, Edit2, Send, Printer, Lock, Globe, Check } from "lucide-react";
import { logEvento, TIPI_EVENTO } from "../utils/logEvento";
import { toLocalDateStr } from "../utils/dateUtils";
import { chiaveBozza, useBozzaModulo } from "../utils/bozzeLocali";
import AvvisoBozza from "../components/AvvisoBozza";

const STATI = {
  bozza:              { label:"Bozza",      bg:"#f3f4f6", col:"#6b7280" },
  definitivo:         { label:"Definitivo", bg:"#d1fae5", col:"#059669" },
  inviato:            { label:"Inviato",    bg:"#dbeafe", col:"#2563eb" },
  inviato_definitivo: { label:"Inviato",    bg:"#1e1b4b", col:"#fff"    },
};
// Verbale bloccato: nessuna modifica possibile dopo invio definitivo
const isBloccato = (v) => v?.stato === "inviato_definitivo";

const inp = {width:"100%",padding:"7px 9px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",boxSizing:"border-box"};
const ta = {...inp, minHeight:80, resize:"vertical"};

// ── PDF verbale ──────────────────────────────────────────────────────────────
function stampaPDF(v, orgInfo) {
  const orgNome = orgInfo?.nome || orgInfo?.name || "";
  const azioni = (v.azioniDaFare||[]).map((a,i)=>`
    <tr style="background:${i%2===0?'#fff':'#f9fafb'}">
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${a.descrizione}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${a.assegnatoA||'—'}</td>
      <td style="padding:8px 12px;border-bottom:1px solid #e5e7eb;">${a.scadenza||'—'}</td>
    </tr>`).join('');

  const conf = (v.conferme||[]).map(c=>`<span style="display:inline-flex;align-items:center;gap:4px;background:#d1fae5;color:#065f46;padding:3px 10px;border-radius:12px;font-size:11px;margin:2px;">✓ ${c.nome}</span>`).join('');
  const partHTML = (v.partecipanti||[]).map(p=>`<span style="display:inline-block;background:#ede9fe;color:#5b21b6;padding:2px 8px;border-radius:10px;font-size:11px;margin:2px;">${p.nome}</span>`).join('');
  const commHTML = (v.commesseCollegate||[]).map(c=>`<span style="display:inline-block;background:#f0f9ff;color:#0369a1;padding:2px 8px;border-radius:10px;font-size:11px;margin:2px;">${c.nome}</span>`).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Verbale — ${v.titolo}</title>
  <style>body{font-family:Arial,sans-serif;color:#111827;margin:0;padding:32px;font-size:13px;}
  h1{font-size:22px;font-weight:800;color:#5b21b6;margin:0 0 4px;}
  h2{font-size:13px;font-weight:700;color:#5b21b6;margin:20px 0 6px;text-transform:uppercase;letter-spacing:.05em;border-bottom:2px solid #ede9fe;padding-bottom:4px;}
  .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #5b21b6;padding-bottom:16px;margin-bottom:20px;}
  .meta{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;background:#f9fafb;border-radius:8px;padding:12px;margin-bottom:16px;}
  .meta-item label{font-size:10px;font-weight:700;color:#9ca3af;text-transform:uppercase;letter-spacing:.06em;display:block;}
  .meta-item span{font-weight:600;color:#111827;}
  .section{margin-bottom:16px;} .section p{line-height:1.7;color:#374151;white-space:pre-wrap;}
  table{width:100%;border-collapse:collapse;} th{background:#5b21b6;color:#fff;padding:8px 12px;text-align:left;font-size:11px;}
  @media print{body{padding:0;}}</style></head><body>
  <div class="header">
    <div>
      ${orgInfo?.logo?`<img src="${orgInfo.logo}" style="height:40px;object-fit:contain;margin-bottom:4px;" />`:`<div style="font-size:18px;font-weight:900;color:#5b21b6;">◆ ${orgNome||'Nexiora'}</div>`}
      <div style="font-size:11px;font-weight:700;color:#374151;">${orgNome}</div>
      ${orgInfo?.indirizzo?`<div style="font-size:9px;color:#6b7280;">${orgInfo.indirizzo}</div>`:''}
      ${orgInfo?.piva?`<div style="font-size:9px;color:#6b7280;">P.IVA ${orgInfo.piva}</div>`:''}
    </div>
    <div style="text-align:right;">
      <h1 style="font-size:18px;font-weight:800;color:#5b21b6;margin-bottom:4px;">${v.titolo}</h1>
      <div style="font-size:11px;color:#6b7280;">
        <div><strong>Data:</strong> ${v.data||''}</div>
        <div><strong>Luogo:</strong> ${v.luogo||'—'}</div>
        <div><strong>Stato:</strong> ${STATI[v.stato]?.label||v.stato}</div>
      </div>
    </div>
  </div>
  <div class="meta">
    <div class="meta-item"><label>Partecipanti</label><div>${partHTML||'—'}</div></div>
    <div class="meta-item"><label>Commesse collegate</label><div>${commHTML||'—'}</div></div>
    <div class="meta-item"><label>Conferme ricevute</label><div>${conf||'—'}</div></div>
  </div>
  ${v.ordineGiorno?`<div class="section"><h2>Ordine del giorno</h2><p>${v.ordineGiorno}</p></div>`:''}
  ${v.discussione?`<div class="section"><h2>Discussione</h2><p>${v.discussione}</p></div>`:''}
  ${v.decisioni?`<div class="section"><h2>Decisioni prese</h2><p>${v.decisioni}</p></div>`:''}
  ${(v.azioniDaFare||[]).length>0?`<div class="section"><h2>Azioni da fare</h2>
    <table><tr><th>Azione</th><th>Assegnato a</th><th>Scadenza</th></tr>${azioni}</table></div>`:''}
  ${v.note?`<div class="section"><h2>Note</h2><p>${v.note}</p></div>`:''}
  <div style="margin-top:48px;border-top:1px solid #e5e7eb;padding-top:16px;font-size:10px;color:#9ca3af;text-align:center;">
    Verbale generato da Nexiora — ${new Date().toLocaleDateString('it-IT')}
  </div>
  </body></html>`;

  const w = window.open('','_blank'); w.document.write(html); w.document.close();
  setTimeout(()=>{
    if (w.closed) return;
    const style = w.document.createElement("style");
    style.textContent = "@media print { .no-print-btn { display:none !important; } }";
    w.document.head.appendChild(style);
    const btn = w.document.createElement("button");
    btn.textContent = "🖨️ Stampa";
    btn.className = "no-print-btn";
    btn.onclick = () => w.print();
    btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
    w.document.body.appendChild(btn);
  }, 50);
}

// ── Email verbale ────────────────────────────────────────────────────────────
function inviaEmail(v, destinatari) {
  const to = destinatari.join(',');
  const subject = encodeURIComponent(`Verbale riunione: ${v.titolo} — ${v.data||''}`);
  const body = encodeURIComponent(
    `Verbale riunione: ${v.titolo}\nData: ${v.data||''}\nLuogo: ${v.luogo||''}\n\n` +
    `Partecipanti: ${(v.partecipanti||[]).map(p=>p.nome).join(', ')}\n\n` +
    (v.ordineGiorno?`ORDINE DEL GIORNO:\n${v.ordineGiorno}\n\n`:'') +
    (v.discussione?`DISCUSSIONE:\n${v.discussione}\n\n`:'') +
    (v.decisioni?`DECISIONI:\n${v.decisioni}\n\n`:'') +
    ((v.azioniDaFare||[]).length>0?`AZIONI DA FARE:\n${v.azioniDaFare.map(a=>`- ${a.descrizione} (${a.assegnatoA||'—'}) — entro ${a.scadenza||'—'}`).join('\n')}\n\n`:'') +
    (v.note?`NOTE:\n${v.note}`:'')
  );
  window.open(`mailto:${to}?subject=${subject}&body=${body}`);
}

// ── Vista lettura verbale ────────────────────────────────────────────────────
function VerbaleReadView({ verbale, currentUser, canEdit, onEdit, onClose, orgInfo }) {
  const { addToast } = useToast();
  const giàConfermato = (verbale.conferme||[]).find(c=>c.uid===currentUser?.uid);
  const èPartecipante = (verbale.partecipanti||[]).find(p=>p.uid===currentUser?.uid);

  const conferma = async () => {
    const nuove = [...(verbale.conferme||[]), {uid:currentUser.uid, nome:currentUser.displayName||currentUser.email, data:new Date().toISOString()}];
    await updateDoc(doc(db,"verbali",verbale.id), {conferme: nuove, updatedAt:serverTimestamp()});
    addToast("Conferma registrata ✅","success");
  };

  const sez = (titolo, testo) => testo ? (
    <div style={{marginBottom:20}}>
      <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",textTransform:"uppercase",letterSpacing:".06em",marginBottom:6,borderBottom:"2px solid #ede9fe",paddingBottom:4}}>{titolo}</div>
      <p style={{fontSize:13,color:"#374151",lineHeight:1.75,whiteSpace:"pre-wrap"}}>{testo}</p>
    </div>
  ) : null;

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:720,maxHeight:"92vh",overflowY:"auto"}}>
        {/* Header */}
        <div style={{background:"linear-gradient(135deg,#5b21b6,#7c3aed)",borderRadius:"8px 8px 0 0",padding:"20px 24px",margin:"-24px -24px 20px",color:"#fff"}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
            <div>
              <div style={{fontSize:11,color:"rgba(255,255,255,.6)",marginBottom:4}}>VERBALE DI RIUNIONE</div>
              <h2 style={{fontSize:18,fontWeight:800,margin:"0 0 8px"}}>{verbale.titolo}</h2>
              <div style={{display:"flex",gap:16,fontSize:12,color:"rgba(255,255,255,.8)"}}>
                <span>📅 {verbale.data?.split("-").reverse().join("/")}</span>
                {verbale.luogo&&<span>📍 {verbale.luogo}</span>}
                <span style={{background:"rgba(255,255,255,.2)",padding:"1px 8px",borderRadius:10,fontSize:11}}>{STATI[verbale.stato]?.label||verbale.stato}</span>
            {isBloccato(verbale) && <span style={{display:"flex",alignItems:"center",gap:4,background:"rgba(255,255,255,.15)",padding:"1px 8px",borderRadius:10,fontSize:11}}><Lock size={9}/> Non modificabile</span>}
              </div>
            </div>
            <div style={{display:"flex",gap:8,marginTop:4}}>
              {canEdit&&<button onClick={onEdit} style={{background:"rgba(255,255,255,.2)",border:"none",borderRadius:6,padding:"5px 10px",color:"#fff",cursor:"pointer",fontSize:11,display:"flex",alignItems:"center",gap:4,fontFamily:"'Inter',sans-serif"}}><Edit2 size={12}/>Modifica</button>}
              <button onClick={onClose} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:6,padding:"5px 8px",color:"#fff",cursor:"pointer"}}><X size={14}/></button>
            </div>
          </div>
        </div>

        {/* Meta */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:20}}>
          <div style={{background:"#faf5ff",borderRadius:8,padding:"12px 14px",border:"1px solid #e9d5ff"}}>
            <div style={{fontSize:10,fontWeight:700,color:"#7c3aed",textTransform:"uppercase",marginBottom:6}}>Partecipanti</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
              {(verbale.partecipanti||[]).map((p,i)=>(
                <span key={i} style={{background:"#ede9fe",color:"#5b21b6",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600}}>{p.nome}</span>
              ))}
              {!(verbale.partecipanti||[]).length&&<span style={{fontSize:11,color:"#9ca3af"}}>Nessuno</span>}
            </div>
          </div>
          <div style={{background:"#f0f9ff",borderRadius:8,padding:"12px 14px",border:"1px solid #bae6fd"}}>
            <div style={{fontSize:10,fontWeight:700,color:"#0369a1",textTransform:"uppercase",marginBottom:6}}>Commesse collegate</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
              {(verbale.commesseCollegate||[]).map((c,i)=>(
                <span key={i} style={{background:"#dbeafe",color:"#1e40af",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600}}>{c.nome}</span>
              ))}
              {!(verbale.commesseCollegate||[]).length&&<span style={{fontSize:11,color:"#9ca3af"}}>Nessuna</span>}
            </div>
          </div>
        </div>

        {/* Conferme */}
        {(verbale.conferme||[]).length>0&&(
          <div style={{background:"#d1fae5",borderRadius:8,padding:"10px 14px",marginBottom:16,border:"1px solid #6ee7b7"}}>
            <div style={{fontSize:10,fontWeight:700,color:"#065f46",textTransform:"uppercase",marginBottom:6}}>✅ Hanno confermato il verbale</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
              {verbale.conferme.map((c,i)=>(
                <span key={i} style={{background:"#fff",color:"#065f46",padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600,border:"1px solid #6ee7b7"}}>✓ {c.nome}</span>
              ))}
            </div>
          </div>
        )}

        {/* Contenuto */}
        {sez("Ordine del giorno", verbale.ordineGiorno)}
        {sez("Discussione", verbale.discussione)}
        {sez("Decisioni prese", verbale.decisioni)}
        {sez("Note", verbale.note)}

        {/* Azioni da fare */}
        {(verbale.azioniDaFare||[]).length>0&&(
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,fontWeight:700,color:"#7c3aed",textTransform:"uppercase",letterSpacing:".06em",marginBottom:8,borderBottom:"2px solid #ede9fe",paddingBottom:4}}>Azioni da fare</div>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead><tr style={{background:"#5b21b6"}}>
                <th style={{padding:"6px 10px",textAlign:"left",color:"#fff",fontWeight:600}}>Azione</th>
                <th style={{padding:"6px 10px",textAlign:"left",color:"#fff",fontWeight:600}}>Assegnato a</th>
                <th style={{padding:"6px 10px",textAlign:"left",color:"#fff",fontWeight:600}}>Scadenza</th>
              </tr></thead>
              <tbody>{verbale.azioniDaFare.map((a,i)=>(
                <tr key={i} style={{background:i%2===0?"#fff":"#faf5ff",borderBottom:"1px solid #f3f4f6"}}>
                  <td style={{padding:"7px 10px",color:"#374151"}}>{a.descrizione}</td>
                  <td style={{padding:"7px 10px",color:"#6b7280"}}>{a.assegnatoA||"—"}</td>
                  <td style={{padding:"7px 10px",color:"#6b7280"}}>{a.scadenza||"—"}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}

        {/* Footer azioni */}
        <div style={{display:"flex",gap:8,flexWrap:"wrap",paddingTop:16,borderTop:"1px solid #f3f4f6"}}>
          {èPartecipante&&!giàConfermato&&(
            <button onClick={conferma}
              style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"#059669",color:"#fff",border:"none",borderRadius:7,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Inter',sans-serif"}}>
              <Check size={13}/> Confermo il verbale
            </button>
          )}
          {giàConfermato&&<span style={{fontSize:12,color:"#059669",fontWeight:600,display:"flex",alignItems:"center",gap:4,padding:"7px 0"}}><Check size={13}/>Hai confermato il verbale</span>}
          <button onClick={()=>stampaPDF(verbale,orgInfo)}
            style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",background:"#f3f4f6",color:"#374151",border:"1px solid #e5e7eb",borderRadius:7,cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif"}}>
            <Printer size={13}/> Stampa / PDF
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Modal email ──────────────────────────────────────────────────────────────
function EmailModal({ verbale, utenti, onClose }) {
  const [selezionati, setSelezionati] = useState(
    (verbale.partecipanti||[]).map(p=>p.uid).filter(Boolean)
  );
  const [emailExtra, setEmailExtra] = useState("");

  const toggleUtente = uid => setSelezionati(s=>s.includes(uid)?s.filter(x=>x!==uid):[...s,uid]);

  const invia = () => {
    const emails = utenti.filter(u=>selezionati.includes(u.uid)).map(u=>u.email||"").filter(Boolean);
    const extra = emailExtra.split(/[,;\s]+/).filter(e=>e.includes("@"));
    inviaEmail(verbale, [...emails,...extra]);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:480}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
          <h3 style={{fontSize:14,fontWeight:700,display:"flex",alignItems:"center",gap:6}}><Send size={14} color="#7c3aed"/>Invia verbale via email</h3>
          <button className="btn-icon" onClick={onClose}><X size={14}/></button>
        </div>
        <div style={{fontSize:12,color:"#6b7280",marginBottom:12}}>Seleziona i destinatari:</div>
        <div style={{maxHeight:200,overflowY:"auto",border:"1px solid #e5e7eb",borderRadius:8,marginBottom:12}}>
          {utenti.map(u=>(
            <label key={u.uid} style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",cursor:"pointer",borderBottom:"1px solid #f3f4f6",fontSize:12}}>
              <input type="checkbox" checked={selezionati.includes(u.uid)} onChange={()=>toggleUtente(u.uid)}/>
              <span style={{fontWeight:600,color:"#374151"}}>{u.displayName}</span>
              <span style={{color:"#9ca3af",fontSize:11}}>{u.email||""}</span>
            </label>
          ))}
        </div>
        <div style={{marginBottom:16}}>
          <label className="form-label">Email aggiuntive (separate da virgola)</label>
          <input style={inp} value={emailExtra} onChange={e=>setEmailExtra(e.target.value)} placeholder="es. cliente@email.it, partner@email.it"/>
        </div>
        <button onClick={invia} className="btn btn-primary" style={{width:"100%",justifyContent:"center",display:"flex",gap:6,alignItems:"center"}}>
          <Send size={13}/> Apri client email
        </button>
        <p style={{fontSize:10,color:"#9ca3af",textAlign:"center",marginTop:8}}>Verrà aperto il tuo client email con i destinatari e il testo precompilati.</p>
      </div>
    </div>
  );
}

// ── Modal nuovo/modifica verbale ─────────────────────────────────────────────
function VerbaleModal({ verbale, commesse, utenti, onSave, onClose }) {
  const oggi = toLocalDateStr(new Date());
  const [form, setForm] = useState(verbale || {
    titolo:"", data:oggi, luogo:"", commesseCollegate:[], partecipanti:[],
    ordineGiorno:"", discussione:"", decisioni:"", azioniDaFare:[],
    stato:"bozza", note:"", visibilita:"tutti"
  });
  const [nuovaAzione, setNuovaAzione] = useState({descrizione:"",assegnatoA:"",assegnatoUid:"",scadenza:"",creaTask:true});
  // Bozza automatica sul dispositivo (utils/bozzeLocali.js)
  const { currentUser } = useAuth();
  const { effectiveOrgId } = useOrg();
  const bozza = useBozzaModulo({
    chiave: chiaveBozza(currentUser?.uid, "verbale", effectiveOrgId, verbale?.id),
    valore: form,
    info: { data: form.data, titolo: `${verbale ? "Modifica verbale" : "Nuovo verbale"}${form.titolo ? ` — ${form.titolo}` : ""}`, vai: { page: "verbali" } },
    applica: d => setForm(f => ({ ...f, ...d })),
  });

  const upd = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleCommessa = (id,nome) => { const l=form.commesseCollegate||[]; upd("commesseCollegate",l.find(c=>c.id===id)?l.filter(c=>c.id!==id):[...l,{id,nome}]); };
  const togglePartecipante = (uid,nome) => { const l=form.partecipanti||[]; upd("partecipanti",l.find(p=>p.uid===uid)?l.filter(p=>p.uid!==uid):[...l,{uid,nome}]); };
  const aggiungiAzione = () => { if(!nuovaAzione.descrizione.trim())return; upd("azioniDaFare",[...(form.azioniDaFare||[]),{...nuovaAzione}]); setNuovaAzione({descrizione:"",assegnatoA:"",assegnatoUid:"",scadenza:"",creaTask:true}); };
  const rimuoviAzione = i => upd("azioniDaFare",form.azioniDaFare.filter((_,idx)=>idx!==i));

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:700,maxHeight:"92vh",overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:700,display:"flex",alignItems:"center",gap:6}}>
            <FileText size={15} color="#7c3aed"/>{verbale?"Modifica verbale":"Nuovo verbale"}
          </h3>
          <button className="btn-icon" onClick={onClose}><X size={15}/></button>
        </div>
        {bozza.trovata && <AvvisoBozza testo={verbale ? "Hai modifiche non salvate a questo verbale" : "Hai un verbale iniziato e non salvato"}
          salvataIl={bozza.trovata.salvataIl} onRiprendi={bozza.riprendi} onElimina={bozza.scarta} />}

        <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr",gap:10,marginBottom:12}}>
          <div><label className="form-label">Titolo riunione *</label>
            <input style={inp} value={form.titolo} onChange={e=>upd("titolo",e.target.value)} placeholder="Es. Riunione avanzamento lavori"/></div>
          <div><label className="form-label">Data *</label>
            <input type="date" style={inp} value={form.data} onChange={e=>upd("data",e.target.value)}/></div>
          <div><label className="form-label">Luogo</label>
            <input style={inp} value={form.luogo} onChange={e=>upd("luogo",e.target.value)} placeholder="Ufficio, Cantiere..."/></div>
        </div>

        {/* Visibilità */}
        <div className="form-group" style={{marginBottom:12}}>
          <label className="form-label">Visibilità</label>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>upd("visibilita","tutti")}
              style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:6,border:`1px solid ${form.visibilita==="tutti"?"#7c3aed":"#e5e7eb"}`,background:form.visibilita==="tutti"?"#ede9fe":"#fff",color:form.visibilita==="tutti"?"#7c3aed":"#6b7280",cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif"}}>
              <Globe size={12}/> Visibile a tutti
            </button>
            <button onClick={()=>upd("visibilita","partecipanti")}
              style={{display:"flex",alignItems:"center",gap:6,padding:"6px 12px",borderRadius:6,border:`1px solid ${form.visibilita==="partecipanti"?"#7c3aed":"#e5e7eb"}`,background:form.visibilita==="partecipanti"?"#ede9fe":"#fff",color:form.visibilita==="partecipanti"?"#7c3aed":"#6b7280",cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif"}}>
              <Lock size={12}/> Solo partecipanti
            </button>
          </div>
        </div>

        {/* Commesse */}
        <div className="form-group" style={{marginBottom:12}}>
          <label className="form-label">Commesse collegate</label>
          <select onChange={e=>{
            const id=e.target.value;
            const comm=commesse.find(x=>x.id===id);
            if(comm&&!(form.commesseCollegate||[]).find(x=>x.id===id))
              upd("commesseCollegate",[...(form.commesseCollegate||[]),{id:comm.id,nome:comm.name}]);
            e.target.value="";
          }} style={{width:"100%",padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:7,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",marginBottom:8,color:"#374151"}}>
            <option value="">— Seleziona commessa —</option>
            {commesse.filter(c=>!(form.commesseCollegate||[]).find(x=>x.id===c.id)).map(c=>(
              <option key={c.id} value={c.id}>{c.codiceCommessa?`[${c.codiceCommessa}] `:""}{c.name}{c.clienteNome?` — ${c.clienteNome}`:""}</option>
            ))}
          </select>
          {(form.commesseCollegate||[]).length>0&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
              {(form.commesseCollegate||[]).map(cc=>(
                <span key={cc.id} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 8px 3px 10px",borderRadius:20,fontSize:11,fontWeight:600,background:"#ede9fe",color:"#7c3aed",border:"1px solid #c4b5fd"}}>
                  {cc.nome}
                  <button type="button" onClick={()=>upd("commesseCollegate",(form.commesseCollegate||[]).filter(x=>x.id!==cc.id))}
                    style={{background:"none",border:"none",cursor:"pointer",color:"#a78bfa",padding:0,lineHeight:1,fontSize:14}}>×</button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Partecipanti */}
        <div className="form-group" style={{marginBottom:12}}>
          <label className="form-label">Partecipanti</label>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {utenti.map(u=>{const s=(form.partecipanti||[]).find(p=>p.uid===u.uid);return(
              <button key={u.uid} onClick={()=>togglePartecipante(u.uid,u.displayName)}
                style={{padding:"3px 10px",borderRadius:12,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"'Inter',sans-serif",border:`1px solid ${s?"#059669":"#e5e7eb"}`,background:s?"#d1fae5":"#fff",color:s?"#059669":"#6b7280"}}>
                {s?"✓ ":""}{u.displayName}
              </button>);})}
          </div>
        </div>

        <div className="form-group"><label className="form-label">Ordine del giorno</label><textarea style={ta} value={form.ordineGiorno} onChange={e=>upd("ordineGiorno",e.target.value)} placeholder="Punti trattati..."/></div>
        <div className="form-group"><label className="form-label">Discussione</label><textarea style={ta} value={form.discussione} onChange={e=>upd("discussione",e.target.value)} placeholder="Cosa è emerso..."/></div>
        <div className="form-group"><label className="form-label">Decisioni prese</label><textarea style={ta} value={form.decisioni} onChange={e=>upd("decisioni",e.target.value)} placeholder="Decisioni concordate..."/></div>

        {/* Azioni */}
        <div className="form-group">
          <label className="form-label">Azioni da fare</label>
          {(form.azioniDaFare||[]).map((a,i)=>(
            <div key={i} style={{display:"flex",gap:6,alignItems:"center",marginBottom:6,background:"#faf5ff",padding:"6px 8px",borderRadius:6,border:"1px solid #e9d5ff"}}>
              <span style={{flex:1,fontSize:11,color:"#374151"}}>{a.descrizione}</span>
              <span style={{fontSize:11,color:"#7c3aed",fontWeight:600}}>{a.assegnatoA||"—"}</span>
              <span style={{fontSize:11,color:"#6b7280"}}>{a.scadenza||"—"}</span>
              {a.creaTask!==false && <span style={{fontSize:9,background:"#d1fae5",color:"#059669",padding:"1px 5px",borderRadius:4,fontWeight:700}}>→ Task</span>}
              <button onClick={()=>rimuoviAzione(i)} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={12}/></button>
            </div>
          ))}
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr auto",gap:6,marginTop:6}}>
            <input style={inp} value={nuovaAzione.descrizione} onChange={e=>setNuovaAzione(n=>({...n,descrizione:e.target.value}))} placeholder="Descrizione azione..."/>
            <select style={inp} value={nuovaAzione.assegnatoUid||""}
              onChange={e=>{
                const u = utenti.find(u=>u.uid===e.target.value);
                setNuovaAzione(n=>({...n,assegnatoUid:e.target.value,assegnatoA:u?.displayName||""}));
              }}>
              <option value="">— Responsabile —</option>
              {utenti.map(u=><option key={u.uid} value={u.uid}>{u.displayName}</option>)}
            </select>
            <input type="date" style={inp} value={nuovaAzione.scadenza} onChange={e=>setNuovaAzione(n=>({...n,scadenza:e.target.value}))}/>
            <button onClick={aggiungiAzione} className="btn btn-primary" style={{padding:"0 12px"}}>+</button>
          </div>
          {/* Opzione crea task */}
          <label style={{display:"flex",alignItems:"center",gap:8,marginTop:8,cursor:"pointer",fontSize:12,color:"#374151"}}>
            <input type="checkbox" checked={nuovaAzione.creaTask!==false}
              onChange={e=>setNuovaAzione(n=>({...n,creaTask:e.target.checked}))}
              style={{accentColor:"#7c3aed"}}/>
            Crea automaticamente un task nel progetto collegato per ogni azione
          </label>
          {(form.commesseCollegate||[]).length===0 && (
            <div style={{fontSize:11,color:"#f59e0b",marginTop:4}}>⚠️ Collega almeno una commessa per generare i task automaticamente.</div>
          )}
        </div>

        <div className="form-group"><label className="form-label">Note</label><textarea style={{...ta,minHeight:60}} value={form.note} onChange={e=>upd("note",e.target.value)} placeholder="Note aggiuntive..."/></div>

        <div style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:8,marginTop:16}}>
          <div/>
          <select value={form.stato} onChange={e=>upd("stato",e.target.value)}
            style={{padding:"7px 10px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none"}}
            disabled={form.stato==="inviato_definitivo"}>
            {Object.entries(STATI).filter(([k])=>k!=="inviato_definitivo").map(([k,s])=><option key={k} value={k}>{s.label}</option>)}
          </select>
          <button onClick={()=>bozza.salvaCon(()=>onSave(form))} className="btn btn-primary"
            disabled={form.stato==="inviato_definitivo"}>
            Salva verbale
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pagina principale ────────────────────────────────────────────────────────
export default function VerbaliPage() {
  const { currentUser, isOrgAdmin, isPlatformOwner } = useAuth();
  const { effectiveOrgId } = useOrg();
  const [orgInfo, setOrgInfo] = useState(null);
  const { addToast } = useToast();
  const canEdit = isOrgAdmin || isPlatformOwner;

  const [verbali, setVerbali] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [commesse, setCommesse] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEmail, setShowEmail] = useState(null);
  const [verbaleEdit, setVerbaleEdit] = useState(null);
  const [verbaleRead, setVerbaleRead] = useState(null);
  const [filtroStato, setFiltroStato] = useState("");
  const [filtroTesto, setFiltroTesto] = useState("");
  const [soloAzioni, setSoloAzioni] = useState(false);

  useEffect(()=>{
    if(!effectiveOrgId)return;
    return onSnapshot(query(collection(db,"verbali"),where("orgId","==",effectiveOrgId)),
      snap=>{
        const tutti=snap.docs.map(d=>({id:d.id,...d.data()}));
        // Filtra per visibilità
        const visibili=tutti.filter(v=>{
          if(v.visibilita==="tutti"||canEdit||isPlatformOwner)return true;
          return (v.partecipanti||[]).find(p=>p.uid===currentUser?.uid);
        });
        visibili.sort((a,b)=>(b.data||"").localeCompare(a.data||""));
        setVerbali(visibili);
      });
  },[effectiveOrgId,currentUser,canEdit,isPlatformOwner]);

  useEffect(()=>{if(!effectiveOrgId)return;return onSnapshot(query(collection(db,"users"),where("organizationId","==",effectiveOrgId),where("enabled","==",true)),snap=>setUtenti(snap.docs.map(d=>d.data())));},[effectiveOrgId]);
  useEffect(()=>{if(!effectiveOrgId)return;return onSnapshot(query(collection(db,"projects"),where("organizationId","==",effectiveOrgId)),snap=>setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.archived&&!p.isParent&&p.statoCommessa!=="chiusa")));},[effectiveOrgId]);

  // Invia verbale in modo definitivo — blocca qualsiasi modifica futura
  const inviaDefinitivo = async (verbale) => {
    if (!await confirm(
      `Inviare il verbale "${verbale.titolo}" in modo definitivo?

Attenzione: una volta inviato non sarà più possibile modificarlo da nessuno.`,
      { title:"Invia verbale definitivo", type:"warning", confirmLabel:"Sì, invia e blocca" }
    )) return;
    await updateDoc(doc(db,"verbali",verbale.id), {
      stato: "inviato_definitivo",
      inviatoIl: serverTimestamp(),
      inviataDA: currentUser?.uid,
      inviatoDaNome: currentUser?.displayName || "",
      updatedAt: serverTimestamp(),
    });
    addToast("Verbale inviato e bloccato ✅ — non modificabile", "success");
  };

  const salva = async form => {
    if(!form.titolo.trim()){addToast("Inserisci il titolo","error");return;}
    const payload = {...form, orgId:effectiveOrgId, updatedAt:serverTimestamp()};
    let verbaleId = verbaleEdit?.id;
    if(verbaleId){
      await updateDoc(doc(db,"verbali",verbaleId),payload);
      addToast("Verbale aggiornato ✅","success");
    } else {
      const ref = await addDoc(collection(db,"verbali"),{...payload,createdAt:serverTimestamp(),conferme:[]});
      verbaleId = ref.id;
      logEvento({ orgId:effectiveOrgId, tipo:"VERBALE_CREATO", ...TIPI_EVENTO.VERBALE_CREATO,
        titolo:`Verbale creato: ${form.titolo}`,
        uid:currentUser?.uid||"", nomeUtente:currentUser?.displayName||"",
        refId:verbaleId, refTipo:"verbale", refNome:form.titolo,
        navigateTo:{page:"verbali"}
      });
      addToast("Verbale creato ✅","success");
    }
    // Genera task per azioni da fare che hanno creaTask:true e una commessa collegata
    const commesseCollegate = form.commesseCollegate||[];
    const projectId = commesseCollegate[0]?.id||null;
    if(projectId && !verbaleEdit?.id){
      // Solo alla creazione — non duplicare se si modifica
      const azioniTask = (form.azioniDaFare||[]).filter(a=>a.creaTask!==false&&a.descrizione.trim());
      for(const azione of azioniTask){
        // Trova la prima sezione del progetto
        const { getDocs:gd, query:q2, collection:c2, where:w2, orderBy:ob } = await import("firebase/firestore");
        const sezioniSnap = await gd(q2(c2(db,"sections"),w2("projectId","==",projectId)));
        const primaSezione = sezioniSnap.docs[0];
        if(!primaSezione) continue;
        await addDoc(collection(db,"tasks"),{
          title: azione.descrizione,
          sectionId: primaSezione.id,
          projectId,
          organizationId: effectiveOrgId,
          assignedTo: azione.assegnatoUid||null,
          assignedName: azione.assegnatoA||null,
          dueDate: azione.scadenza||null,
          status: "aperta",
          priority: "media",
          fromVerbale: verbaleId,
          createdAt: serverTimestamp(),
        });
      }
      if(azioniTask.length>0){
        logEvento({ orgId:effectiveOrgId, tipo:"VERBALE_TASK_GENERATI", ...TIPI_EVENTO.VERBALE_TASK_GENERATI,
          titolo:`Verbale: ${azioniTask.length} task generati da "${form.titolo}"`,
          uid:currentUser?.uid||"", nomeUtente:currentUser?.displayName||"",
          refId:verbaleId, refTipo:"verbale", refNome:form.titolo,
          projectId, projectName:commesseCollegate[0]?.nome||"",
          navigateTo:{page:"project",projectId}
        });
        addToast(`${azioniTask.length} task creati dal verbale ✅`,"success");
      }
    }
    setShowModal(false); setVerbaleEdit(null);
  };

  const elimina = async id => {
    if (!await confirm("Eliminare questo verbale?", { type:"danger" })) return;
    await deleteDoc(doc(db,"verbali",id));
    addToast("Verbale eliminato","success");
  };

  const apriModifica = v => {
    if (isBloccato(v)) {
      addToast("Verbale inviato in modo definitivo — non è possibile modificarlo", "warning");
      return;
    }
    setVerbaleEdit(v); setVerbaleRead(null); setShowModal(true);
  };
  const apriLettura = v => { setVerbaleRead(v); setShowModal(false); };

  const visibili = verbali.filter(v=>{
    if(soloAzioni && !(v.azioniDaFare||[]).length)return false;
    if(filtroStato&&v.stato!==filtroStato)return false;
    if(filtroTesto&&!v.titolo.toLowerCase().includes(filtroTesto.toLowerCase()))return false;
    return true;
  });

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      {/* Header */}
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"12px 20px",flexShrink:0}}>
        <div style={{marginBottom:10}}>
          <h2 style={{fontSize:16,fontWeight:700,display:"flex",alignItems:"center",gap:8}}>
            <FileText size={17} color="#7c3aed"/> Verbali di Riunione
            {canEdit&&(
              <button onClick={()=>{setVerbaleEdit(null);setShowModal(true);}} className="btn btn-primary" style={{fontSize:12,padding:"4px 12px",display:"flex",alignItems:"center",gap:6}}>
                <Plus size={13}/> Nuovo verbale
              </button>
            )}
          </h2>
        </div>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          <input value={filtroTesto} onChange={e=>setFiltroTesto(e.target.value)} placeholder="🔍 Cerca verbale..." style={{...inp,width:220,padding:"5px 10px"}}/>
          <button onClick={()=>setSoloAzioni(v=>!v)}
            style={{padding:"5px 12px",border:"1px solid "+(soloAzioni?"#d97706":"#e5e7eb"),borderRadius:8,
              background:soloAzioni?"#fef3c7":"#fff",color:soloAzioni?"#92400e":"#374151",
              fontSize:12,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap"}}>
            📋 Ha azioni da fare
          </button>
          <select value={filtroStato} onChange={e=>setFiltroStato(e.target.value)} style={{...inp,width:160,padding:"5px 10px"}}>
            <option value="">Tutti gli stati</option>
            {Object.entries(STATI).map(([k,s])=><option key={k} value={k}>{s.label}</option>)}
          </select>
        </div>
      </div>

      {/* Lista */}
      <div style={{flex:1,overflowY:"auto",padding:16}}>
        {visibili.length===0&&(
          <div style={{textAlign:"center",padding:48,color:"#9ca3af"}}>
            <FileText size={32} style={{margin:"0 auto 12px",opacity:.3}}/>
            <p>Nessun verbale trovato</p>
          </div>
        )}
        {visibili.map(v=>{
          const st=STATI[v.stato]||STATI.bozza;
          const conferme=v.conferme||[];
          const partecipanti=v.partecipanti||[];
          const tuttiConfermato=partecipanti.length>0&&partecipanti.every(p=>conferme.find(c=>c.uid===p.uid));
          const ioHoConfermato=conferme.find(c=>c.uid===currentUser?.uid);
          return (
            <div key={v.id} style={{background:"#fff",border:"1px solid #e5e7eb",borderRadius:10,padding:"14px 16px",marginBottom:10,transition:"box-shadow .2s"}}
              onMouseOver={e=>e.currentTarget.style.boxShadow="0 2px 12px rgba(124,58,237,.1)"}
              onMouseOut={e=>e.currentTarget.style.boxShadow="none"}>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:12}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4,flexWrap:"wrap"}}>
                    <span style={{fontSize:14,fontWeight:700,color:"#111827"}}>{v.titolo}</span>
                    <span style={{padding:"1px 8px",borderRadius:10,fontSize:11,fontWeight:700,background:st.bg,color:st.col}}>{st.label}</span>
                    {v.visibilita==="partecipanti"&&<span style={{display:"flex",alignItems:"center",gap:3,fontSize:10,color:"#9ca3af"}}><Lock size={9}/>Solo partecipanti</span>}
                    {tuttiConfermato&&<span style={{fontSize:10,color:"#059669",fontWeight:700}}>✅ Tutti confermato</span>}
                    {ioHoConfermato&&!tuttiConfermato&&<span style={{fontSize:10,color:"#059669"}}>✓ Hai confermato</span>}
                  </div>
                  <div style={{display:"flex",gap:12,fontSize:11,color:"#6b7280",flexWrap:"wrap"}}>
                    <span>📅 {v.data?.split("-").reverse().join("/")}</span>
                    {v.luogo&&<span>📍 {v.luogo}</span>}
                    {partecipanti.length>0&&<span>👥 {partecipanti.map(p=>p.nome).join(", ")}</span>}
                    {(v.commesseCollegate||[]).length>0&&<span>🔗 {v.commesseCollegate.map(c=>c.nome).join(", ")}</span>}
                  </div>
                  {conferme.length>0&&partecipanti.length>0&&(
                    <div style={{marginTop:4,fontSize:11,color:"#6b7280"}}>
                      Conferme: {conferme.length}/{partecipanti.length}
                    </div>
                  )}
                </div>
                <div style={{display:"flex",gap:6,flexShrink:0}}>
                  <button onClick={()=>apriLettura(v)}
                    style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",border:"1px solid #e5e7eb",borderRadius:6,background:"#fff",color:"#374151",cursor:"pointer",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
                    <Eye size={12}/> Leggi
                  </button>
                  {canEdit&&<>
                    {!isBloccato(v) && (
                      <>
                        <button onClick={()=>apriModifica(v)}
                          style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",border:"1px solid #e5e7eb",borderRadius:6,background:"#fff",color:"#374151",cursor:"pointer",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
                          <Edit2 size={12}/> Modifica
                        </button>
                        <button onClick={()=>inviaDefinitivo(v)}
                          style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",border:"1px solid #7c3aed",borderRadius:6,background:"#ede9fe",color:"#7c3aed",cursor:"pointer",fontSize:11,fontFamily:"'Inter',sans-serif",fontWeight:600}}>
                          <Send size={12}/> Invia
                        </button>
                      </>
                    )}
                    {isBloccato(v) && (
                      <span style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",fontSize:11,color:"#fff",background:"#1e1b4b",borderRadius:6,fontWeight:600}}>
                        <Lock size={11}/> Bloccato
                      </span>
                    )}
                    <button onClick={()=>setShowEmail(v)}
                      style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",border:"1px solid #dbeafe",borderRadius:6,background:"#eff6ff",color:"#2563eb",cursor:"pointer",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
                      <Send size={12}/> Email
                    </button>
                    <button onClick={()=>stampaPDF(v,orgInfo)}
                      style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",border:"1px solid #e5e7eb",borderRadius:6,background:"#fff",color:"#374151",cursor:"pointer",fontSize:11,fontFamily:"'Inter',sans-serif"}}>
                      <Printer size={12}/> PDF
                    </button>
                    {!isBloccato(v) && (
                      <button onClick={()=>elimina(v.id)}
                        style={{padding:"5px 8px",border:"1px solid #fee2e2",borderRadius:6,background:"#fff",color:"#dc2626",cursor:"pointer",fontSize:11}}>
                        <X size={12}/>
                      </button>
                    )}
                  </>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showModal&&<VerbaleModal verbale={verbaleEdit} commesse={commesse} utenti={utenti} onSave={salva} onClose={()=>{setShowModal(false);setVerbaleEdit(null);}}/>}
      {verbaleRead&&<VerbaleReadView verbale={verbaleRead} currentUser={currentUser} canEdit={canEdit} onEdit={()=>apriModifica(verbaleRead)} onClose={()=>setVerbaleRead(null)} orgInfo={orgInfo}/>}
      {showEmail&&<EmailModal verbale={showEmail} utenti={utenti} onClose={()=>setShowEmail(null)}/>}
    </div>
  );
}
